import { isDemoMode, setDemoMode } from '@/storage/demoMode';
import { trackAnalyticsEvent } from '@/composables/useAnalytics';
import { isAuthPageName, resolveAuthenticatedLandingPath } from '@/utils/authRedirect';

export const authenticatedNewsRoute = (route) => {
  const name = { 'public-news': 'news', 'public-news-article': 'news-profiteia-article' }[route.name];
  return name ? { name, params: route.params, query: route.query, hash: route.hash, replace: true } : null;
};

const getSessionStorage = () => (typeof window === 'undefined' ? null : window.sessionStorage);
const getLocalStorage = () => (typeof window === 'undefined' ? null : window.localStorage);

export const routeRequiresAuth = (route) =>
  route.meta.requiresAuth === true || (route.meta.requiresAuth !== false && !route.meta.formScreen);

export const isPublicNonAuthRoute = (route) =>
  route.meta.requiresAuth === false && !isAuthPageName(route.name);

const TRIAL_EXPIRED_ALLOWED_ROUTE_NAMES = new Set([
  'subscriptions',
  'subscriptions-upgrade-flex',
  'subscriptions-upgrade-tools',
  'subscriptions-upgrade-basic',
  'stripe.subscribed-callback',
]);

// Billing states that mean a payment problem has hard-blocked the account
// (mirrors BillingAccountState::grantsAccess() === false for these on the
// backend). `grace_period` is intentionally excluded — it's a soft block
// that still grants access, so those users keep roaming the app and only
// see the dunning banner. `canceled`/`none` flow through the normal
// trial/subscribe funnel above.
const BILLING_PAYMENT_BLOCKED_STATES = new Set(['past_due', 'unpaid']);

// Routes a payment-blocked account may still reach so it can actually fix
// the problem (and so the redirect below never loops on its own target).
const BILLING_FIX_ALLOWED_ROUTE_NAMES = new Set([
  'my-subscription',
  'subscription.invoices',
  'subscriptions',
  'subscriptions-upgrade-flex',
  'subscriptions-upgrade-tools',
  'subscriptions-upgrade-basic',
  'stripe.subscribed-callback',
]);

export function createInitialAuthRestoreController({
  router,
  authStore,
  sessionStorage = getSessionStorage(),
  localStorage = getLocalStorage(),
  waitIntervalMs = 50,
  timeoutMs = 3000,
}) {
  const state = {
    initialAuthCheckComplete: false,
    initialAuthCheckTimedOut: false,
  };

  const redirectAuthenticatedUserFromAuthPage = () => {
    const currentRoute = router.currentRoute.value;
    if (!currentRoute || !authStore.isAuthenticated || (!isAuthPageName(currentRoute.name) && !authenticatedNewsRoute(currentRoute))) {
      return;
    }

    const newsRoute = authenticatedNewsRoute(currentRoute);
    if (newsRoute) { router.replace(newsRoute).catch(() => {}); return; }
    const redirectPath = resolveAuthenticatedLandingPath(currentRoute.query?.redirect, {
      apiOnly: authStore.isApiOnly,
    });
    if (redirectPath === currentRoute.path) {
      return;
    }

    router.replace({ path: redirectPath }).catch(() => {});
  };

  const markComplete = () => {
    state.initialAuthCheckComplete = true;

    if (state.initialAuthCheckTimedOut) {
      redirectAuthenticatedUserFromAuthPage();
    }
  };

  const start = () => {
    authStore.hydrateCachedUser();

    if (sessionStorage?.getItem('accessToken') || localStorage?.getItem('refreshToken')) {
      authStore.getUserInformation({ silent: true }).finally(markComplete);
    } else {
      state.initialAuthCheckComplete = true;
    }
  };

  const waitForInitialAuthCheck = async (route) => {
    if (state.initialAuthCheckComplete || (isPublicNonAuthRoute(route) && !authenticatedNewsRoute(route))) {
      return;
    }

    await new Promise((resolve) => {
      let checkInterval = null;
      let timeoutId = null;

      const finishWait = () => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        resolve();
      };

      checkInterval = setInterval(() => {
        if (state.initialAuthCheckComplete) {
          finishWait();
        }
      }, waitIntervalMs);

      timeoutId = setTimeout(() => {
        state.initialAuthCheckTimedOut = true;
        state.initialAuthCheckComplete = true;
        finishWait();
      }, timeoutMs);
    });
  };

  return {
    state,
    start,
    waitForInitialAuthCheck,
  };
}

export function createAuthGuard({ authStore, authRestore }) {
  return async (to) => {
    await authRestore.waitForInitialAuthCheck(to);

    const requiresAuth = routeRequiresAuth(to);
    const isAuthenticated = authStore.isAuthenticated;
    if (isAuthenticated || to.name === 'login') setDemoMode(false);
    else if ((to.query?.demo === '1' || new URLSearchParams(window.location.search).get('demo') === '1') && !isDemoMode()) {
      setDemoMode(true);
      trackAnalyticsEvent('demo.entered', { resource_type: 'demo', resource_id: String(to.name || '') });
    }
    const demo = !isAuthenticated && isDemoMode();

    if (isAuthenticated && authenticatedNewsRoute(to)) return authenticatedNewsRoute(to);

    if (isAuthenticated && isAuthPageName(to.name)) {
      const redirectPath = resolveAuthenticatedLandingPath(to.query.redirect, {
        apiOnly: authStore.isApiOnly,
      });
      return { path: redirectPath };
    }

    const demoToolEntry = {
      'bidding-strategy': 'bidding-strategy-new',
      'imbalance-benchmark': 'imbalance-benchmark-new',
      'forward-benchmark': 'forward-benchmark-new',
      'shadow-trading': 'shadow-trading-new',
    };
    if (demo && demoToolEntry[to.name]) return { name: demoToolEntry[to.name] };

    // Private pages stay browsable as previews without mounting their account requests.
    if (demo && requiresAuth && !to.meta.demo && !to.path?.startsWith('/dev')) {
      return { name: 'demo-preview', query: { feature: String(to.meta.title || '') } };
    }

    if (requiresAuth && !isAuthenticated && !(demo && to.meta.demo)) {
      return {
        name: 'login',
        query: { redirect: to.fullPath },
      };
    }

    if (
      requiresAuth &&
      isAuthenticated &&
      authStore.trialExpired &&
      !authStore.hasValidSubscription &&
      !TRIAL_EXPIRED_ALLOWED_ROUTE_NAMES.has(to.name)
    ) {
      return {
        name: 'subscriptions',
        query: { trial: 'expired' },
      };
    }

    // A missed/failed payment has blocked the account: funnel the user to the
    // billing page where they can fix it, instead of leaving them on pages the
    // API will reject anyway.
    if (
      requiresAuth &&
      isAuthenticated &&
      BILLING_PAYMENT_BLOCKED_STATES.has(authStore.billingAccountState) &&
      !BILLING_FIX_ALLOWED_ROUTE_NAMES.has(to.name)
    ) {
      return {
        name: 'subscription.invoices',
        query: { billing: authStore.billingAccountState },
      };
    }

    return undefined;
  };
}
